import React, { useState, useContext, memo, useCallback, useMemo } from 'react';
import { PageContext } from '../PageContext';
import { Page, UserProfile } from '../types';
import { isUserOnline } from '../utils';
import { useAdmin } from '../AdminContext';
import { useAuth } from '../AuthContext';
import BackButton from '../components/BackButton';

// User Item for List
const UserResultItem: React.FC<{
    user: UserProfile;
    onAddFriend: (uid: string) => void;
    onFollow: (uid: string) => void;
    isFriend: boolean;
    isFollowing: boolean;
    requestSent: boolean;
    onUserClick: (user: UserProfile) => void;
    mutualCount?: number;
}> = memo(({ user, onAddFriend, onFollow, isFriend, isFollowing, requestSent, onUserClick, mutualCount }) => (
    <div onClick={() => onUserClick(user)} className="flex items-center p-3 space-x-3 bg-white rounded-lg shadow-sm cursor-pointer hover:bg-gray-50">
        <div className="relative flex-shrink-0">
            <img src={user.photoURL} alt={user.displayName} className="w-12 h-12 rounded-full object-cover" />
            {isUserOnline(user) && (
                <span className="absolute bottom-0 right-0 block h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
            )}
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{user.displayName}</p>
            {mutualCount !== undefined && mutualCount > 0 ? (
                <p className="text-xs text-purple-600 font-semibold">{mutualCount} Mutual Friends</p>
            ) : (
                <p className="text-sm text-gray-500 truncate">{user.bio}</p>
            )}
        </div>
        <div className="flex-shrink-0 flex items-center space-x-2">
            {isFriend ? (
                <button
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        if (window.confirm(`Are you sure you want to unfriend ${user.displayName}?`)) {
                            onFollow(user.uid);
                        }
                    }}
                    className="bg-red-100 text-red-600 border border-red-200 font-bold text-xs px-3 py-1 rounded-md hover:bg-red-200 transition-colors"
                >
                    Unfriend
                </button>
            ) : isFollowing ? (
                 <button
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        if (window.confirm("Cancel friend request?")) {
                            onFollow(user.uid);
                        }
                    }}
                    className="bg-gray-200 text-gray-600 font-bold text-xs px-3 py-1 rounded-md hover:bg-gray-300"
                >
                    Request Sent
                </button>
            ) : (
                <button
                    onClick={(e) => { e.stopPropagation(); onAddFriend(user.uid); }}
                    disabled={requestSent}
                    className="bg-[var(--accent-pink)] text-white font-bold text-xs px-3 py-1 rounded-md disabled:bg-gray-400 hover:bg-pink-600"
                >
                    {requestSent ? 'Sending...' : 'Add Friend'}
                </button>
            )}
        </div>
    </div>
));

interface FriendsListPageProps {
    initialTab?: 'friends' | 'suggestions';
}

const FriendsListPage: React.FC<FriendsListPageProps> = ({ initialTab }) => {
    const pageContext = useContext(PageContext);
    const { sendFriendRequest, sentRequests } = useAdmin();
    const { userProfile, toggleFollow, allUsers } = useAuth();
    const [activeTab, setActiveTab] = useState<'friends' | 'suggestions'>(initialTab || 'friends');

    // "Friends" are mutual follows for this app's logic
    const friends = useMemo(() => {
        if (!userProfile) return [];
        return allUsers.filter(u => userProfile.following.includes(u.uid) && userProfile.followers.includes(u.uid));
    }, [userProfile, allUsers]);
    
    // "Suggestions" - People You May Know
    const suggestions = useMemo(() => {
        if (!userProfile) return [];
        
        const friendIds = new Set(friends.map(f => f.uid));
        
        const candidates = allUsers.filter(u => 
            u.uid !== userProfile.uid && 
            !friendIds.has(u.uid) &&
            !userProfile.following.includes(u.uid)
        );

        const scoredCandidates = candidates.map(candidate => {
            const mutuals = userProfile.following.filter(id => candidate.followers.includes(id));
            return {
                user: candidate,
                mutualCount: mutuals.length
            };
        });

        let result = scoredCandidates.filter(c => c.mutualCount > 0).sort((a, b) => b.mutualCount - a.mutualCount);
        
        if (result.length < 5) {
             const randoms = candidates
                .filter(c => !result.some(r => r.user.uid === c.uid))
                .sort(() => 0.5 - Math.random()) // Shuffle
                .slice(0, 5 - result.length)
                .map(c => ({ user: c, mutualCount: 0 }));
            result = [...result, ...randoms];
        }

        return result;
    }, [userProfile, allUsers, friends]);

    const handleUserClick = useCallback((user: UserProfile) => {
        pageContext?.switchPage(Page.Profile, user.displayName, { userId: user.uid });
    }, [pageContext]);

    if (!userProfile) {
        return <p className="text-center text-gray-500 pt-8">Could not load user profile.</p>;
    }

    const activeTabClasses = "bg-white/10 backdrop-blur-sm";
    const inactiveTabClasses = "bg-transparent text-white/70";

    return (
        <div className="space-y-4">
            <BackButton text="Back to Profile" />
            
            <div className="flex justify-between items-center px-2">
                <h2 className="text-2xl font-bold aurora-text">My Network</h2>
            </div>

            <div className="bg-[var(--primary-dark-purple)] p-1 rounded-xl flex space-x-1">
                <button onClick={() => setActiveTab('friends')} className={`flex-1 py-2 font-bold rounded-lg transition-all duration-300 ${activeTab === 'friends' ? activeTabClasses : inactiveTabClasses}`}>
                     <span className={activeTab === 'friends' ? 'aurora-text' : ''}>Friends ({friends.length})</span>
                </button>
                <button onClick={() => setActiveTab('suggestions')} className={`flex-1 py-2 font-bold rounded-lg transition-all duration-300 ${activeTab === 'suggestions' ? activeTabClasses : inactiveTabClasses}`}>
                     <span className={activeTab === 'suggestions' ? 'aurora-text' : ''}>Suggestions</span>
                </button>
            </div>
            
            <div className="space-y-2">
                {activeTab === 'friends' && (
                    friends.length > 0 ? (
                        friends.map(user => (
                            <UserResultItem
                                key={user.uid}
                                user={user}
                                onAddFriend={sendFriendRequest}
                                onFollow={toggleFollow}
                                isFriend={true}
                                isFollowing={true}
                                requestSent={sentRequests.has(user.uid)}
                                onUserClick={handleUserClick}
                            />
                        ))
                    ) : (
                        <p className="text-center text-gray-500 py-8">No friends to show. Use search to find people!</p>
                    )
                )}
                {activeTab === 'suggestions' && (
                    suggestions.length > 0 ? (
                        suggestions.map(({ user, mutualCount }) => (
                            <UserResultItem
                                key={user.uid}
                                user={user}
                                onAddFriend={sendFriendRequest}
                                onFollow={toggleFollow}
                                isFriend={false}
                                isFollowing={userProfile.following.includes(user.uid)}
                                requestSent={sentRequests.has(user.uid)}
                                onUserClick={handleUserClick}
                                mutualCount={mutualCount}
                            />
                        ))
                    ) : (
                        <p className="text-center text-gray-500 py-8">No new suggestions right now. Check back later!</p>
                    )
                )}
            </div>
        </div>
    );
};

export default FriendsListPage;
